// src/services/mediaService.js
import { generatedMediaList } from '../data/generated-media.js';

// Fonction utilitaire pour obtenir les médias disponibles
export async function getAvailableMedia() {
  return generatedMediaList;
}

// Service complet pour la gestion des médias
export class MediaService {
  constructor() {
    this.baseMediaPath = '/media';
    this.supportedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    this.supportedVideoTypes = ['video/mp4', 'video/webm', 'video/ogg'];
    this.maxFileSize = 50 * 1920 * 1080; // 50MB
  }

  // Obtenir la liste des médias disponibles dans le dossier public
  async getAvailableMedia() {
    try {
      // Utilise d'abord la liste générée, puis ajoute les médias statiques
      const generatedMedia = generatedMediaList || [];
      
      // Liste statique des médias disponibles dans public/media/
      const staticMediaList = [
        {
          id: 'cafe-menu-1',
          name: 'cafe-menu.jpg',
          type: 'image',
          category: 'menu',
          path: '/media/images/cafe-menu.jpg',
          size: '2.3 MB',
          dimensions: '1920x1080',
          uploadDate: '2025-06-15T10:00:00Z',
          tags: ['menu', 'café', 'petit-déjeuner']
        },
        {
          id: 'croissant-1',
          name: 'croissant.jpg',
          type: 'image',
          category: 'food',
          path: '/media/images/croissant.jpg',
          size: '1.8 MB',
          dimensions: '1280x720',
          uploadDate: '2025-06-14T14:30:00Z',
          tags: ['croissant', 'viennoiserie', 'boulangerie']
        },
        {
          id: 'logo-grange',
          name: 'logo.png',
          type: 'image',
          category: 'branding',
          path: '/media/images/logo.png',
          size: '0.5 MB',
          dimensions: '512x512',
          uploadDate: '2025-06-10T09:00:00Z',
          tags: ['logo', 'marque', 'identité']
        },
        {
          id: 'vitrine-1',
          name: 'vitrine.jpg',
          type: 'image',
          category: 'ambiance',
          path: '/media/images/vitrine.jpg',
          size: '3.1 MB',
          dimensions: '1920x1080',
          uploadDate: '2025-06-12T16:20:00Z',
          tags: ['vitrine', 'extérieur', 'façade']
        },
        {
          id: 'promo-noel',
          name: 'promo-noel.jpg',
          type: 'image',
          category: 'promotion',
          path: '/media/images/promo-noel.jpg',
          size: '2.7 MB',
          dimensions: '1920x1080',
          uploadDate: '2025-06-01T11:45:00Z',
          tags: ['noël', 'promotion', 'fêtes']
        },
        {
          id: 'cafe-ambiance',
          name: 'cafe-ambiance.jpg',
          type: 'image',
          category: 'ambiance',
          path: '/media/images/cafe-ambiance.jpg',
          size: '2.1 MB',
          dimensions: '1920x1080',
          uploadDate: '2025-06-08T13:15:00Z',
          tags: ['ambiance', 'intérieur', 'café']
        },
        {
          id: 'vitrine-demo-video',
          name: 'vitrine-demo.mp4',
          type: 'video',
          category: 'demo',
          path: '/media/videos/vitrine-demo.mp4',
          size: '15.2 MB',
          duration: '0:30',
          uploadDate: '2025-06-05T15:00:00Z',
          tags: ['démo', 'vidéo', 'présentation']
        },
        {
          id: 'pain-frais',
          name: 'pain-frais.jpg',
          type: 'image',
          category: 'food',
          path: '/media/images/pain-frais.jpg',
          size: '1.9 MB',
          dimensions: '1280x720',
          uploadDate: '2025-06-13T08:30:00Z',
          tags: ['pain', 'boulangerie', 'frais']
        }
      ];

      // Combine les médias générés et statiques
      return [...generatedMedia, ...staticMediaList];
    } catch (error) {
      console.error('Erreur lors du chargement des médias:', error);
      return [];
    }
  }

  // Obtenir l'URL complète d'un média
  getMediaUrl(mediaPath) {
    return process.env.PUBLIC_URL + mediaPath;
  }

  // Valider un fichier média
  async validateFile(file) {
    const errors = [];

    // Vérifier la taille
    if (file.size > this.maxFileSize) {
      errors.push(`Le fichier est trop volumineux (max ${this.formatFileSize(this.maxFileSize)})`);
    }

    // Vérifier le type
    const allSupportedTypes = [...this.supportedImageTypes, ...this.supportedVideoTypes];
    if (!allSupportedTypes.includes(file.type)) {
      errors.push('Format de fichier non supporté');
    }

    // Vérification supplémentaire pour les images
    if (this.supportedImageTypes.includes(file.type)) {
      try {
        const dimensions = await this.getImageDimensions(file);
        if (dimensions.width < 100 || dimensions.height < 100) {
          errors.push('L\'image est trop petite (minimum 100x100px)');
        }
      } catch (error) {
        errors.push('Impossible de lire les dimensions de l\'image');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Obtenir les dimensions d'une image
  getImageDimensions(file) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight
        });
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Impossible de charger l\'image'));
      };

      img.src = url;
    });
  }

  // Générer une miniature
  async generateThumbnail(file, maxWidth = 200, maxHeight = 200) {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        // Calculer les nouvelles dimensions en conservant le ratio
        const ratio = Math.min(maxWidth / img.width, maxHeight / img.height);
        const newWidth = img.width * ratio;
        const newHeight = img.height * ratio;

        canvas.width = newWidth;
        canvas.height = newHeight;

        // Dessiner l'image redimensionnée
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        // Convertir en base64
        const thumbnailDataUrl = canvas.toDataURL('image/jpeg', 0.8);
        URL.revokeObjectURL(url);
        resolve(thumbnailDataUrl);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };

      img.src = url;
    });
  }

  // Compresser une image
  async compressImage(file, quality = 0.8, maxWidth = 1920, maxHeight = 1080) {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        // Calculer les nouvelles dimensions
        let { width, height } = img;
        
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio;
          height *= ratio;
        }

        canvas.width = width;
        canvas.height = height;

        // Dessiner et compresser
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob((compressedBlob) => {
          URL.revokeObjectURL(url);
          resolve(compressedBlob);
        }, 'image/jpeg', quality);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(file); // Retourner le fichier original en cas d'erreur
      };

      img.src = url;
    });
  }

  // Formater la taille de fichier
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Filtrer les médias par catégorie
  filterByCategory(mediaList, category) {
    if (!category || category === 'all') return mediaList;
    return mediaList.filter(media => media.category === category);
  }

  // Filtrer les médias par type
  filterByType(mediaList, type) {
    if (!type || type === 'all') return mediaList;
    return mediaList.filter(media => media.type === type);
  }

  // Rechercher dans les médias
  searchMedia(mediaList, query) {
    if (!query) return mediaList;
    
    const lowercaseQuery = query.toLowerCase();
    return mediaList.filter(media =>
      media.name.toLowerCase().includes(lowercaseQuery) ||
      media.tags.some(tag => tag.toLowerCase().includes(lowercaseQuery)) ||
      media.category.toLowerCase().includes(lowercaseQuery)
    );
  }

  // Trier les médias
  sortMedia(mediaList, sortBy = 'name', order = 'asc') {
    const sorted = [...mediaList].sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Traitement spécial pour les dates
      if (sortBy === 'uploadDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      // Traitement spécial pour la taille
      if (sortBy === 'size') {
        aValue = this.parseFileSize(aValue);
        bValue = this.parseFileSize(bValue);
      }

      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }

  // Parser la taille de fichier pour le tri
  parseFileSize(sizeString) {
    const units = { 'Bytes': 1, 'KB': 1024, 'MB': 1024 * 1024, 'GB': 1024 * 1024 * 1024 };
    const match = sizeString.match(/^([\d.]+)\s*(\w+)$/);
    
    if (!match) return 0;
    
    const value = parseFloat(match[1]);
    const unit = match[2];
    
    return value * (units[unit] || 1);
  }

  // Obtenir les catégories disponibles
  getCategories(mediaList) {
    const categories = [...new Set(mediaList.map(media => media.category))];
    return categories.sort();
  }

  // Obtenir les statistiques des médias
  getMediaStats(mediaList) {
    const totalFiles = mediaList.length;
    const totalSize = mediaList.reduce((sum, media) => sum + this.parseFileSize(media.size), 0);
    const imageCount = mediaList.filter(media => media.type === 'image').length;
    const videoCount = mediaList.filter(media => media.type === 'video').length;
    const categories = this.getCategories(mediaList);

    return {
      totalFiles,
      totalSize: this.formatFileSize(totalSize),
      imageCount,
      videoCount,
      categories: categories.length
    };
  }

  // Simuler l'upload d'un fichier (pour la démo)
  async simulateUpload(file) {
    // Validation
    const validation = await this.validateFile(file);
    if (!validation.isValid) {
      throw new Error(validation.errors.join(', '));
    }

    // Simulation d'upload avec progression
    return new Promise((resolve) => {
      let progress = 0;
      const interval = setInterval(() => {
        progress += Math.random() * 20;
        
        if (progress >= 100) {
          clearInterval(interval);
          
          // Créer un objet média simulé
          const uploadedMedia = {
            id: `uploaded-${Date.now()}`,
            name: file.name,
            type: file.type.startsWith('image') ? 'image' : 'video',
            category: 'uploaded',
            path: `/media/uploads/${file.name}`,
            size: this.formatFileSize(file.size),
            uploadDate: new Date().toISOString(),
            tags: ['uploadé', 'nouveau']
          };

          resolve({
            success: true,
            media: uploadedMedia,
            progress: 100
          });
        }
      }, 200);
    });
  }

  // Obtenir les médias récents
  getRecentMedia(mediaList, limit = 5) {
    return mediaList
      .sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate))
      .slice(0, limit);
  }

  // Obtenir les médias par tags
  getMediaByTags(mediaList, tags) {
    if (!tags || tags.length === 0) return mediaList;
    
    return mediaList.filter(media =>
      tags.some(tag => media.tags.includes(tag))
    );
  }
}

// Instance par défaut du service
export const mediaService = new MediaService();

// Fonctions utilitaires exportées
export const {
  getMediaUrl,
  validateFile,
  generateThumbnail,
  compressImage,
  formatFileSize,
  filterByCategory,
  filterByType,
  searchMedia,
  sortMedia,
  getCategories,
  getMediaStats,
  simulateUpload,
  getRecentMedia,
  getMediaByTags
} = mediaService;